import {
    ChangeDetectionStrategy,
    Component, EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { Store } from '@ngrx/store';
import { BsModalService } from 'ngx-bootstrap/modal';
import { SelectItem } from 'primeng/api/selectitem';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs/index';
import { filter, map } from 'rxjs/operators';
import { AuthenticationSelectors } from '../auth/state/authentication.selectors';
import { BsModalConfirmComponent, createConfirmModalOptions } from '../core/modal/modal-confirm.component';
import { Provider } from '../core/model/central/provider.model';
import { Reason } from '../core/model/central/reason.model';
import { User } from '../core/model/user.model';
import { LocationEntity } from '../locations/state/location-entity.model';
import { LocationsService } from '../locations/state/locations.service';
import { ReasonsSettingsActions } from '../reasons/state/reasons-settings.actions';
import { ReasonsSettingsSelectors } from '../reasons/state/reasons-settings.selectors';
import { ProviderService } from '../reminders/reminder-settings/state/provider.service';
import { AppointmentResourceSettingsActions } from '../resources/state/appointment-resource-settings.actions';
import { AppointmentResource } from '../resources/state/appointment-resource-settings.model';
import { AppointmentResourceSettingsSelectors } from '../resources/state/appointment-resource-settings.selectors';
import { AdaptiveRule } from './state/adaptive-rule.model';

@Component({
    selector: 'current-rule',
    templateUrl: './current-rule.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CurrentRuleComponent implements OnInit, OnChanges, OnDestroy {

    @Input() selectedUserId;
    @Input() allRules: Array<AdaptiveRule>;
    @Input() selectedRule: AdaptiveRule;
    @Output() readonly mapChanged: EventEmitter<AdaptiveRule> = new EventEmitter<AdaptiveRule>();
    @Output() readonly clearRule: EventEmitter<AdaptiveRule> = new EventEmitter<AdaptiveRule>();

    public refrenceTypes: Array<SelectItem>;
    selectedReference: any;
    updatedReference: any;
    name: string;
    displayReferenceType: Array<SelectItem> = [];
    subscription: Subscription = new Subscription();
    isUpdated = false;
    defaultRule: string;
    mediaTypes: Array<SelectItem> = [{
        label: 'Email',
        value: 'email'
    },{
        label: 'SMS',
        value: 'sms'
    },
        {
            label: 'TwilioVoice',
            value: 'twiliovoice'
        }];
    selectedMedia: any;
    updatedMedia: any;
    onClose: Subject<boolean>;
    viewModel$: Observable<any>;
    providers$: Observable<Array<Provider>>;
    reasons$: Observable<Array<Reason>>;
    locations$: Observable<Array<string>>;
    locationEntities$: Observable<Array<LocationEntity>>;
    currentUser$: Observable<User>;
    appointmentResources$: Observable<Array<AppointmentResource>>;

    // When tried to send a message on Friday ( dayOfWeek 5) thymeleaf takes it as dayOfWeek 6 and displays Saturday instead
    dayOfWeek: Array<any> = [
    { label: 'Sunday', value: '1'},
    {
        label: 'Monday',
        value: '2'
    },{
        label: 'Tuesday',
        value: '3'
    },{
        label: 'Wednesday',
        value: '4'
    },{
        label: 'Thursday',
        value: '5'
    },{
        label: 'Friday',
        value: '6'
    },{
        label: 'Saturday',
        value: '7'
    }
    ];


    constructor(
                private providerService: ProviderService,
                private authenticationSelectors: AuthenticationSelectors,
                private reasonsSettingsSelectors: ReasonsSettingsSelectors,
                private locationsService: LocationsService,
                private appointmentResourceSettingsSelectors: AppointmentResourceSettingsSelectors,
                private modalService: BsModalService,
                private store: Store<any>) {
        this.providers$ = this.providerService.entities$;
        this.reasons$ = this.reasonsSettingsSelectors.selectReasons();
        this.appointmentResources$ = this.appointmentResourceSettingsSelectors.selectAppointmentResources();
        this.currentUser$ = this.authenticationSelectors.selectCurrentUser();

    }

    ngOnInit(): void {

        if (this.selectedUserId === undefined) {
            this.subscription.add(this.providerService.loaded$
                .pipe(
                    filter(loaded => !loaded)
                )
                .subscribe(loaded => this.providerService.getAll())
            );

            this.subscription.add(this.currentUser$
                .pipe(
                    filter(currentUser => currentUser !== undefined))
                .subscribe(currentUser => {
                        this.locations$ = this.locationsService.getLocations(currentUser.userId);
                    }
                ));

            this.subscription.add(this.currentUser$
                .pipe(
                    filter(currentUser => currentUser !== undefined))
                .subscribe(currentUser => {
                        this.locationEntities$ = this.locationsService.getLocationEntities(currentUser.userId, false);
                    }
                ));

            this.store.dispatch(AppointmentResourceSettingsActions.getAppointmentResourcesAndResources(undefined));
            this.store.dispatch(ReasonsSettingsActions.getRemindersReasons(undefined));
        } else {
            this.store.dispatch(AppointmentResourceSettingsActions.getAppointmentResourcesAndResources({userid: this.selectedUserId}));
            this.store.dispatch(ReasonsSettingsActions.getRemindersReasons({userId: this.selectedUserId}));

            this.locations$ = this.locationsService.getLocations(this.selectedUserId);
            this.locationEntities$ = this.locationsService.getLocationEntities(this.selectedUserId, false);

            this.providers$ = this.providerService.getProvidersByUserId(this.selectedUserId);
        }
        this.setRefrenceTypes();
        this.onClose = new Subject();

        this.viewModel$ = combineLatest([
            this.providers$,
            this.locations$,
            this.reasons$,
            this.appointmentResources$,
            this.locationEntities$]
        )
            .pipe(
                map(([providers, locations, reasons, appointmentResources, locationEntities]) => {
                    if (providers !== undefined && providers.length > 0) {
                        if (this.displayReferenceType.findIndex(reference => reference.label === 'Provider') === -1) {
                            this.displayReferenceType.push({
                                label: 'Provider',
                                value: 'provider'
                            });
                        }
                    }

                    if (locations !== undefined && locations.length > 0) {
                        if (this.displayReferenceType
                           .findIndex(reference => reference.label === 'Location (by appointment location name)') === -1) {
                            this.displayReferenceType.push({
                                label: 'Location (by appointment location name)',
                                value: 'location'
                            });
                        }
                    }

                    if (locationEntities !== undefined && locationEntities.length > 0) {
                       if (this.displayReferenceType
                          .findIndex(reference => reference.label === 'Location (by appointment location id)') === -1) {
                          this.displayReferenceType.push({
                             label: 'Location (by appointment location id)',
                             value: 'appointmentLocationId'
                          });
                       }

                       if (this.displayReferenceType
                          .findIndex(reference => reference.label === 'Location (by client location id)') === -1) {
                          this.displayReferenceType.push({
                             label: 'Location (by client location id)',
                             value: 'personLocationId'
                          });
                       }
                    }

                    if (reasons !== undefined && reasons.length > 0) {
                        if (this.displayReferenceType.findIndex(reference => reference.label === 'Reason') === -1) {
                            this.displayReferenceType.push({
                                label: 'Reason',
                                value: 'reason'
                            });
                        }
                    }

                    if (appointmentResources !== undefined && appointmentResources.length > 0) {
                        if (this.displayReferenceType.findIndex(reference => reference.label === 'Appointment Resource') === -1) {
                            this.displayReferenceType.push({
                                label: 'Appointment Resource',
                                value: 'resource'
                            });
                        }
                    }

                    if (this.displayReferenceType.findIndex(reference => reference.label === 'DayOfWeek') === -1) {
                        this.displayReferenceType.push({
                            label: 'DayOfWeek',
                            value: 'dayOfWeek'
                        });
                    }

                    if (this.displayReferenceType.findIndex(reference => reference.label === 'DayOfWeekOfAppointment') === -1) {
                        this.displayReferenceType.push({
                            label: 'DayOfWeekOfAppointment',
                            value: 'dayOfWeekOfAppointment'
                        });
                    }

                    return({
                        providers, locations, reasons, appointmentResources, locationEntities
                    });
                })
            );

        if (this.selectedMedia === undefined) {
            this.selectedMedia = 'email';
        }
    }

    setRefrenceTypes(): void {
        this.refrenceTypes = [
            {
                label : 'Appointment Resource',
                value : 'resource'
            },
            {
                label : 'Reason',
                value : 'reason'
            },
            {
                label : 'Provider',
                value : 'provider'
            },
            {
                label : 'Location (by appointment location name)',
                value : 'location'
            },
            {
               label : 'Location (by appointment location id)',
               value : 'appointmentLocationId'
            },
            {
               label : 'Location (by client location id)',
               value : 'personLocationId'
            },
            {
                label : 'DayOfWeek',
                value : 'dayOfWeek'
            },
            {
                label : 'DayOfWeekOfAppointment',
                value : 'dayOfWeekOfAppointment'
            }
        ];
    }

    setSelectedReference(reference): void {
        if (this.isUpdated) {
            const modalRef = this.modalService
                .show(BsModalConfirmComponent, createConfirmModalOptions('Confirm',
                    'You will loose all unsaved changes. Are you sure you want to change?',
                    'Yes', 'No'));

            modalRef.content.onClose
                .subscribe(dialogResult => {
                    if (dialogResult.isConfirmed) {
                        this.isUpdated = false;
                        this.selectedReference = reference;
                        this.onClose.next(dialogResult);
                        this.mapChanged.emit(undefined);
                    } else {
                        this.updatedReference = this.selectedReference;
                        this.mapChanged.emit(undefined);
                    }
                });
        } else {
            this.selectedReference = reference;
        }
    }

    setSelectedMedia(media): void {
        if (this.isUpdated) {
            const modalRef = this.modalService
                .show(BsModalConfirmComponent, createConfirmModalOptions('Confirm',
                    'You will loose all unsaved changes. Are you sure you want to change?',
                    'Yes', 'No'));

            modalRef.content.onClose
                .subscribe(dialogResult => {
                    if (dialogResult.isConfirmed) {
                        this.isUpdated = false;
                        this.selectedMedia = media;
                        this.onClose.next(dialogResult);
                        this.mapChanged.emit(undefined);
                    } else {
                        this.updatedMedia = this.selectedMedia;
                        this.mapChanged.emit(undefined);
                    }
                });
        } else {
            this.selectedMedia = media;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.selectedRule !== undefined) {
            this.defaultRule = undefined;
            if (this.selectedRule !== undefined && this.selectedRule.reference !== undefined) {
                if (this.selectedRule.ruleName !== undefined && this.selectedRule.ruleName !== '') {
                    this.name = this.selectedRule.ruleName;
                    this.defaultRule = this.selectedRule.default;
                }
                if (this.selectedRule.reference !== undefined) {
                    this.selectedReference = this.selectedRule.reference;
                }

                this.selectedMedia = this.selectedRule.mediaType;
            }
            if (this.selectedRule === undefined && changes.selectedRule.previousValue !== undefined) {
                this.name = undefined;
                this.defaultRule = undefined;
                this.selectedReference = undefined;
                this.selectedMedia = undefined;
            }
            this.updatedReference = this.selectedReference;
            this.updatedMedia = this.selectedMedia;
        }
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    onMapChange(updatedRule: AdaptiveRule): void {

        this.name = this.name.toString().replace(/[^a-zA-Z0-9-_]*$/g, '');

        let isNewRule = true;
        this.isUpdated = false;
        if (updatedRule.type !== undefined && updatedRule.type === 'map') {
            this.allRules.map(userRule => {
                if (updatedRule.ruleName === userRule.ruleName) {
                    let isMapUpdated = false; // if value in map array is updated
                    updatedRule.map.findIndex(rule => {
                        const key = Object.keys(rule)[0];
                        const value = Object.values(rule)[0];
                        userRule.map.find(userrule => {
                            const userrulekey = Object.keys(userrule)[0];
                            const userrulevalue = Object.values(userrule)[0];
                            if (userrulekey === key && !isMapUpdated) {
                                isMapUpdated = userrulevalue !== value;
                            }
                        })
                    });
                    this.isUpdated = updatedRule.mediaType !== userRule.mediaType ||
                        updatedRule.default !== userRule.default || isMapUpdated ||
                        updatedRule.map.length !== userRule.map.length;

                    isNewRule = false;
                }
                return userRule;
            });

            if (this.isUpdated || isNewRule) {
                if (isNewRule) {
                    this.isUpdated = true;
                }
                this.mapChanged.emit(updatedRule);
            }
        }
    }

    defaultChanged(defaultVal: string): void {
        if (this.selectedMedia !== undefined && this.selectedMedia === 'voice') {
            if (defaultVal !== undefined && defaultVal !== null) {
                const matches = defaultVal.toString().match(/[a-zA-Z]/g);
                if (matches !== null) {
                    defaultVal = defaultVal.toString().replace(/[a-zA-Z]/g, '');
                }
            }
        }
        this.defaultRule = defaultVal;
    }

    checkIfExists(): boolean {
        return (this.selectedRule.ruleName !== undefined  && this.selectedRule.ruleName === '') &&
                (this.allRules !== undefined && this.allRules.length !== 0 ?
                this.allRules.findIndex(rules => rules.ruleName === this.name) !== -1 : false);
    }

    public clearRuleDetails(): void {
        if (this.isUpdated) {
            const modalRef = this.modalService
                .show(BsModalConfirmComponent, createConfirmModalOptions('Confirm',
                    'You will loose all unsaved changes. Are you sure you want to change?',
                    'Yes', 'No'));

            modalRef.content.onClose
                .subscribe(dialogResult => {
                    if (dialogResult.isConfirmed) {
                        this.isUpdated = false;
                        this.selectedRule = undefined;
                        this.onClose.next(dialogResult);
                        this.clearRule.emit(undefined);
                    } else {
                        this.mapChanged.emit(undefined);
                    }
                });
        } else {
            this.selectedRule = undefined;
            this.clearRule.emit(undefined);
        }
    }

}
